import { parse } from 'qs';

// Config.
import filterServiceTypeOptions from '@/config/filter-service-type-options';
import filterSourceOfFundsOptions from '@/config/filter-source-of-funds-options';

// Services.
import flattenFilters from '@/services/filters/flatten-filters';

/**
 * Given a URL search string (query string), parse it into a filters object,
 * applying defaults as necessary.
 * @param {string} search - The URL search (query) string.
 * @returns {object} The parsed filters object.
 */
const parseFiltersFromUrl = (search) => {
  const {
    outputType = '$billions',
    serviceType = ['allTypes'],
    sourceOfFunds = ['allSources'],
    tab = 0,
    yearCompare = ['*', '*'],
    yearRange = ['*', '*'],
    yearSingle = '*',
    yearType = 'range',
  } = parse(search.replace(/^\?/, ''));

  // Build filters from parsed query string.
  const filters = {
    outputType,
    serviceType,
    sourceOfFunds,
    tab,
    yearCompare,
    yearRange,
    yearSingle,
    yearType,
  };

  // Ensure tabs are integers.
  if (!Number.isInteger(filters.tab)) {
    filters.tab = parseInt(filters.tab, 10);
  }

  /**
   * Helper function for ensuring year values are integers.
   * @param {string|number} year - The year value to filter.
   * @returns {string|number} An asterisk if provided with an asterisk, otherwise an integer.
   */
  const filterYear = (year) => (year === '*' ? year : parseInt(year, 10));

  // Ensure yearCompare and yearRange are asterisks or integers.
  ['yearCompare', 'yearRange'].forEach((property) => {
    filters[property] = filters[property].map(filterYear);
  });

  // Ensure yearSingle is an asterisk or an integer.
  filters.yearSingle = filterYear(filters.yearSingle);

  // If sourceOfFunds is not exactly [allSources], disallow population-adjusted modes.
  if ((!filters.sourceOfFunds.includes('allSources') || filters.sourceOfFunds.length > 1) && filters.outputType.includes('pop')) {
    filters.outputType = '$billions';
  }

  // Restrict number of service types or sources of funds based on which tab is active.
  if (filters.tab % 2 === 0) {
    filters.sourceOfFunds = filters.sourceOfFunds.slice(0, 1);
  } else {
    filters.serviceType = filters.serviceType.slice(0, 1);
  }

  // Process filter suppression.
  [
    {
      flattenedFilters: flattenFilters(filterServiceTypeOptions),
      key: 'serviceType',
    },
    {
      flattenedFilters: flattenFilters(filterSourceOfFundsOptions),
      key: 'sourceOfFunds',
    },
  ].forEach(({ flattenedFilters, key }) => {
    // Remove any filters that are suppressed for this key on the current tab.
    filters[key] = filters[key].filter((item) => {
      const filterConfig = flattenedFilters.find(({ slug }) => slug === item);
      return !filterConfig
        || !filterConfig.suppress
        || !filterConfig.suppress.includes(filters.tab);
    });

    // If the above suppression results in an empty filter, use the first non-suppressed item.
    if (filters[key].length === 0) {
      const nonSuppressed = flattenedFilters
        .find(({ suppress }) => !suppress.includes(filters.tab));
      if (nonSuppressed) {
        filters[key] = [nonSuppressed.slug];
      }
    }
  });

  return filters;
};

export default parseFiltersFromUrl;
